import { createPortal } from 'react-dom'
import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { formatReal } from '@cannect/utils/formatters'
import {
  TProgressiveDiscountType,
  formatShippingTierLabel,
  getDisplayableTiers,
  useGetProductTiers
} from '@cannect/services/resources/progressive-discounts'

export type ProgressiveTierShape = {
  min_quantity: number
  discount_percent: number
  open_ended?: boolean
  unit_final_price?: number
  /** Tipo do abatimento de FRETE desta faixa ('percent'|'value'). Presente quando a regra beneficia frete. */
  shipping_discount_type?: TProgressiveDiscountType
  /** Valor do abatimento de FRETE desta faixa (percentual ou absoluto, conforme shipping_discount_type). */
  shipping_value?: number
}

const GREEN = '#748351'

function findActiveTier(tiers: ProgressiveTierShape[], qty: number): ProgressiveTierShape | undefined {
  return tiers.filter((t) => t.min_quantity <= qty).pop()
}

/**
 * Retorna o preço unitário efetivo para a quantidade atual.
 * Aplica o discount_percent da faixa ativa sobre fallbackPrice (preço da linha do carrinho),
 * NÃO sobre o preço-catálogo da API (unit_final_price/unit_price). Isso garante consistência
 * com o valor real exibido na linha. Ex.: base 100, faixa 20% → 80,00.
 */
export function useProgressiveUnitPrice(productId: number, quantity: number, fallbackPrice: number): number {
  const { data: tiersData } = useGetProductTiers(productId)
  if (!tiersData?.has_progressive_discount) return fallbackPrice
  const isShipping = !!(
    tiersData.rule?.benefit_target &&
    tiersData.rule.benefit_target !== 'item' &&
    tiersData.rule.benefit_target !== 'both'
  )
  if (isShipping) return fallbackPrice
  const displayTiers = getDisplayableTiers(tiersData.tiers).sort((a, b) => a.min_quantity - b.min_quantity)
  const activeTier = displayTiers.filter((t) => t.min_quantity <= quantity).pop()
  if (!activeTier) return fallbackPrice
  const pct = Number(activeTier.discount_percent) || 0
  return pct > 0 ? Number((fallbackPrice * (1 - pct / 100)).toFixed(2)) : fallbackPrice
}

interface ProgressiveQuantityDropdownProps {
  qty: number
  /** Faixas exibíveis (discount > 0), ordenadas crescentemente. Vazio = sem desconto progressivo. */
  tiers: ProgressiveTierShape[]
  mode: 'group' | 'single'
  /** Total do grupo — necessário somente no mode 'group'. */
  groupQty?: number
  /** Preço unitário base da LINHA do item (ex.: unitPrice/real_price do carrinho). Usado no preview de preço por opção. NÃO é o preço-catálogo da API. */
  unitPrice?: number
  /** Estoque disponível — define o teto do range para itens sem faixas. */
  stock?: number
  open: boolean
  onToggle: () => void
  onSelect: (n: number) => void
  onRemove?: () => void
  /**
   * true → renderiza o popover via React Portal (position:fixed ancorado ao trigger).
   * Necessário quando um ancestral tem overflow:hidden/scroll que cortaria o popover absolute.
   * false/omitido → comportamento padrão (position:absolute) — não regredir o carrinho amplo.
   */
  overlay?: boolean
  /** true → regra exclusiva de frete (benefit_target !== 'item' e !== 'both'): oculta o "% OFF" por opção e não aplica desconto no preço do item. */
  isShipping?: boolean
}

export function ProgressiveQuantityDropdown({
  qty,
  tiers,
  mode,
  groupQty,
  unitPrice,
  stock,
  open,
  onToggle,
  onSelect,
  onRemove,
  overlay = false,
  isShipping = false
}: ProgressiveQuantityDropdownProps) {
  const hasTiers = tiers.length > 0
  const maxTier = hasTiers ? tiers[tiers.length - 1] : undefined
  const maxOption = maxTier ? maxTier.min_quantity + 6 : Math.max(stock || 0, 20)
  const options = Array.from({ length: maxOption }, (_, i) => i + 1)

  // Ref para o botão trigger — ancora o portal no espaço da viewport
  const triggerRef = useRef<HTMLButtonElement>(null)
  // Ref para o popover portalado — distingue scroll INTERNO da lista (não deve fechar) do scroll da página
  const popoverRef = useRef<HTMLDivElement>(null)
  // Posição viewport do popover quando overlay=true
  const [portalPos, setPortalPos] = useState<{ top: number; left: number } | null>(null)

  // Captura o rect do trigger imediatamente antes do paint (evita flash de posição errada)
  useLayoutEffect(() => {
    if (!overlay) return
    if (open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect()
      // Centra o popover de 420px abaixo do botão; clampa para não sair da viewport
      const idealLeft = r.left + r.width / 2 - 210
      const left = Math.max(8, Math.min(idealLeft, window.innerWidth - 428))
      setPortalPos({ top: r.bottom + 10, left })
    } else {
      setPortalPos(null)
    }
  }, [overlay, open])

  // Fecha o popover quando o usuário rola a página (popover fixed ficaria desancorado).
  // Ignora o scroll disparado pela própria lista de opções (dentro do popover) — senão o scroll
  // interno nunca chega a rolar: o 'scroll' é capturado no window (capture:true) antes de mover a lista.
  const onToggleRef = useRef(onToggle)
  onToggleRef.current = onToggle
  useEffect(() => {
    if (!overlay || !open) return undefined
    const close = (e: Event) => {
      if (popoverRef.current && e.target instanceof Node && popoverRef.current.contains(e.target)) return
      onToggleRef.current()
    }
    window.addEventListener('scroll', close, { capture: true, passive: true })
    return () => window.removeEventListener('scroll', close, { capture: true })
  }, [overlay, open])

  // Conteúdo interno do popover (compartilhado entre os dois modos de renderização)
  const popoverInner = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <svg
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke={GREEN}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round">
          <path d="M7.5 3h6l7.5 7.5a2 2 0 0 1 0 2.8l-4.7 4.7a2 2 0 0 1-2.8 0L6 10.5V4.5A1.5 1.5 0 0 1 7.5 3z" />
        </svg>
        <h4 style={{ fontSize: 15, fontWeight: 700, color: '#3f3f3f' }}>Escolha a quantidade</h4>
      </div>

      {hasTiers && mode === 'group' && groupQty !== undefined && (
        <p style={{ fontSize: 12.5, lineHeight: 1.45, color: '#8a8a8a', marginBottom: 12 }}>
          A faixa é definida pela <strong style={{ color: GREEN }}>soma do grupo</strong> ({groupQty} un no total).
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 260, overflowY: 'auto' }}>
        {options.map((n) => {
          const effectiveQty = mode === 'group' && groupQty !== undefined ? groupQty - qty + n : n
          const tier = hasTiers ? findActiveTier(tiers, effectiveQty) : undefined
          const selected = n === qty
          const newGroupQty = mode === 'group' && groupQty !== undefined ? effectiveQty : undefined

          let optionUnitFinalPrice: number | undefined
          let itemOffLabel: string | undefined
          let shippingOffLabel: string | undefined
          if (hasTiers && unitPrice !== undefined) {
            const pct = tier && !isShipping ? Number(tier.discount_percent) || 0 : 0
            optionUnitFinalPrice = pct > 0 ? Number((unitPrice * (1 - pct / 100)).toFixed(2)) : unitPrice
          }
          if (hasTiers) {
            if (!isShipping && tier && Number(tier.discount_percent) > 0) {
              itemOffLabel = `${tier.discount_percent}% OFF`
            }
            if (tier && Number(tier.shipping_value) > 0) {
              shippingOffLabel = formatShippingTierLabel(tier)
            }
            if (!itemOffLabel && !shippingOffLabel) {
              itemOffLabel = 'sem desconto'
            }
          }
          const priceFmt =
            optionUnitFinalPrice !== undefined ? (formatReal(optionUnitFinalPrice) as string) || undefined : undefined

          const rowStyle: CSSProperties = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            width: '100%',
            textAlign: 'left',
            padding: '11px 12px',
            borderRadius: 11,
            border: selected ? '1px solid #cfd8ac' : '1px solid transparent',
            background: selected ? '#f1f4e6' : 'transparent',
            cursor: 'pointer',
            transition: 'all .15s ease'
          }

          const badgeStyle: CSSProperties = {
            display: 'inline-block',
            padding: '3px 9px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            color: '#fff',
            background: selected ? GREEN : '#a6b482',
            whiteSpace: 'nowrap'
          }

          return (
            <button key={n} type="button" onClick={() => onSelect(n)} style={rowStyle}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    flex: 'none',
                    border: selected ? `5px solid ${GREEN}` : '2px solid #cfcfcf',
                    boxSizing: 'border-box'
                  }}
                />
                <span style={{ fontSize: 14, fontWeight: 600, color: '#3f3f3f' }}>{n} un</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {newGroupQty !== undefined && (
                  <span style={{ fontSize: 11.5, color: '#9a9a9a' }}>grupo {newGroupQty} un</span>
                )}
                {priceFmt && <span style={{ fontSize: 12, color: '#5a5a5a', fontWeight: 500 }}>{priceFmt}/un</span>}
                {itemOffLabel && <span style={badgeStyle}>{itemOffLabel}</span>}
                {shippingOffLabel && <span style={badgeStyle}>{shippingOffLabel}</span>}
              </span>
            </button>
          )
        })}
      </div>
    </>
  )

  // Estilos comuns para o container do popover (compartilhados entre absolute e fixed)
  const popoverBaseStyle: CSSProperties = {
    width: 420,
    background: '#fff',
    border: '1px solid #e7e6da',
    borderRadius: 16,
    boxShadow: '0 24px 50px -18px rgba(63,63,50,.45)',
    padding: 12
  }

  // Seta decorativa no topo do popover
  const arrowEl = (
    <div
      style={{
        position: 'absolute',
        top: -7,
        left: '50%',
        transform: 'translateX(-50%) rotate(45deg)',
        width: 13,
        height: 13,
        background: '#fff',
        borderLeft: '1px solid #e7e6da',
        borderTop: '1px solid #e7e6da'
      }}
    />
  )

  return (
    <>
      <div
        style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}
        data-dd>
        {/* Botão disparador */}
        <button
          ref={triggerRef}
          type="button"
          onClick={onToggle}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            border: `1.5px solid ${open ? GREEN : '#cfcfcf'}`,
            borderRadius: 9,
            background: '#fff',
            minWidth: 74,
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'border-color .16s ease'
          }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#3f3f3f', whiteSpace: 'nowrap' }}>{qty} un</span>
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke={GREEN}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ transition: 'transform .18s ease', transform: `rotate(${open ? 180 : 0}deg)`, flexShrink: 0 }}>
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            style={{
              background: 'none',
              border: 'none',
              color: '#c47a3d',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer'
            }}>
            remover
          </button>
        )}

        {/* Popover no modo padrão (position:absolute) — não regredir o carrinho amplo */}
        {!overlay && open && (
          <div
            style={{
              ...popoverBaseStyle,
              position: 'absolute',
              top: 'calc(100% + 10px)',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 60
            }}>
            {arrowEl}
            {popoverInner}
          </div>
        )}
      </div>

      {/* Popover no modo portal (position:fixed) — escapa de overflow de ancestral no drawer */}
      {overlay &&
        open &&
        portalPos &&
        createPortal(
          <div
            ref={popoverRef}
            data-dd
            style={{
              ...popoverBaseStyle,
              position: 'fixed',
              top: portalPos.top,
              left: portalPos.left,
              zIndex: 9999
            }}>
            {arrowEl}
            {popoverInner}
          </div>,
          document.body
        )}
    </>
  )
}

interface ProgressiveSingleQuantityControlProps {
  productId: number
  qty: number
  onSelect: (n: number) => void
  onRemove?: () => void
  /** Estoque do produto — limita o range quando não há desconto progressivo. */
  stock?: number
  /** Preço unitário base da linha do item — passado ao dropdown para preview de preço por opção. */
  basePrice?: number
  /** true → renderiza o popover via portal (necessário no drawer/sheet para escapar de overflow). */
  overlay?: boolean
}

/**
 * Controle de quantidade unificado via dropdown para todos os itens.
 * Itens COM desconto progressivo: exibe faixas com badge e preview de preço.
 * Itens SEM desconto: dropdown simples, range 1..stock (ou 20).
 * Nunca mostra fallback — sempre um dropdown.
 */
export function ProgressiveSingleQuantityControl({
  productId,
  qty,
  onSelect,
  onRemove,
  stock,
  basePrice,
  overlay = false
}: ProgressiveSingleQuantityControlProps) {
  const { data: tiersData } = useGetProductTiers(productId)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return undefined
    const handleDown = (e: MouseEvent) => {
      if (!(e.target as Element).closest('[data-dd]')) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleDown)
    return () => document.removeEventListener('mousedown', handleDown)
  }, [open])

  const hasProgressiveDiscount = tiersData?.has_progressive_discount ?? false
  const isShipping = !!(
    tiersData?.rule?.benefit_target &&
    tiersData.rule.benefit_target !== 'item' &&
    tiersData.rule.benefit_target !== 'both'
  )
  const displayTiers = hasProgressiveDiscount
    ? [...getDisplayableTiers(tiersData!.tiers)].sort((a, b) => a.min_quantity - b.min_quantity)
    : []

  return (
    <ProgressiveQuantityDropdown
      qty={qty}
      tiers={displayTiers}
      mode="single"
      unitPrice={basePrice}
      stock={stock}
      open={open}
      overlay={overlay}
      isShipping={isShipping}
      onToggle={() => setOpen((prev) => !prev)}
      onSelect={(n) => {
        onSelect(n)
        setOpen(false)
      }}
      onRemove={onRemove}
    />
  )
}
